// ─────────────────────────────────────────────
//  crnaRoomGuard — "one CRNA, one room" rule (OB is the exception).
//
//  OB CRNAs run obstetrics 24h and can legitimately cover more than one room
//  (e.g. step into a Main-OR case while still holding WC OB). EVERY other CRNA
//  is a single-room provider — being the primary `ahpId` in two rooms at once
//  is a scheduling mistake (double-booked).
//
//  OB is identified BY ROOM (decision 2026-06-30): a CRNA counts as OB when any
//  room they are the primary ahp of resolves to an OB-location room
//  (canonicalKey(room.location) === "ob"). So a non-OB CRNA who picks up a
//  second non-OB room is flagged; an OB CRNA never is.
//
//  This is a WARN, not a hard block: the AIC / a doctor may override (some days
//  genuinely need it). The UI surfaces the conflict; it never refuses the write.
//
//  Pure — no React, no Firestore.
// ─────────────────────────────────────────────
import { canonicalKey, isTbdRoomName } from "./locations";
import { roomForOrRoom } from "./gridTemplate";

// Does this assignment's orRoom resolve to an OB-location room? Falls back to a
// name heuristic (the live sheet's "WC OB") when no room doc matches yet.
export function isObAssignment(assignment, rooms = []) {
  const orRoom = assignment && assignment.orRoom;
  if (!orRoom) return false;
  const room = roomForOrRoom(orRoom, rooms);
  if (room) return canonicalKey(room.location) === "ob";
  return /\bob\b/i.test(orRoom);
}

// A "real" primary CRNA assignment we count toward the one-room rule: has an
// ahpId, sits in a named non-TBD room. (TBD is an internal bench placeholder.)
function isCountableCrnaRow(a) {
  return !!(a && a.ahpId && a.orRoom && !isTbdRoomName(a.orRoom));
}

// Group countable assignments by CRNA → { ahpId, ahpName, rooms:Set, isOB }.
function crnasByRoom(assignments = [], rooms = []) {
  const byCrna = new Map();
  for (const a of assignments) {
    if (!isCountableCrnaRow(a)) continue;
    let entry = byCrna.get(a.ahpId);
    if (!entry) {
      entry = { ahpId: a.ahpId, ahpName: a.ahpName || "", rooms: new Set(), isOB: false };
      byCrna.set(a.ahpId, entry);
    }
    entry.rooms.add(a.orRoom);
    if (a.ahpName && !entry.ahpName) entry.ahpName = a.ahpName;
    if (isObAssignment(a, rooms)) entry.isOB = true;
  }
  return byCrna;
}

/**
 * Every non-OB CRNA who is the primary ahp in MORE THAN ONE distinct room.
 * @returns {Array<{ ahpId, ahpName, rooms: string[] }>}  rooms = distinct names.
 */
export function crnaRoomConflicts(assignments = [], rooms = []) {
  const out = [];
  for (const entry of crnasByRoom(assignments, rooms).values()) {
    if (entry.isOB) continue; // OB CRNAs may hold many rooms
    if (entry.rooms.size > 1) {
      out.push({ ahpId: entry.ahpId, ahpName: entry.ahpName, rooms: [...entry.rooms] });
    }
  }
  return out;
}

// Set of ahpIds in conflict — handy for marking grid rows.
export function conflictedCrnaIds(assignments = [], rooms = []) {
  return new Set(crnaRoomConflicts(assignments, rooms).map((c) => c.ahpId));
}

/**
 * Would assigning `ahpId` to `orRoom` double-book a non-OB CRNA? Used by the
 * grid/import BEFORE a write to warn (not block). Returns the conflict detail or
 * null. `excludeId` skips the row being edited so re-saving the same row is fine.
 */
export function wouldConflict(assignments, rooms, { ahpId, orRoom, excludeId = null }) {
  if (!ahpId || !orRoom || isTbdRoomName(orRoom)) return null;
  // The CRNA's other countable rooms (excluding the row being saved).
  const otherRooms = new Set();
  let candidateIsOB = isObAssignment({ orRoom }, rooms);
  for (const a of assignments || []) {
    if (excludeId && a.id === excludeId) continue;
    if (a.ahpId !== ahpId || !isCountableCrnaRow(a)) continue;
    if (a.orRoom === orRoom) continue;
    otherRooms.add(a.orRoom);
    if (isObAssignment(a, rooms)) candidateIsOB = true;
  }
  if (candidateIsOB) return null; // OB CRNA — multi-room allowed
  if (otherRooms.size === 0) return null; // first room — fine
  return { ahpId, rooms: [orRoom, ...otherRooms] };
}
